/**
 * Navigate's sections and the open switches they replace (2026-10-05,
 * app-study.html: "every destination opens IN it").
 *
 * Seven sections already had their own open switch, used from many places:
 * Settings (and its Look and Backup), Connect, Pedals and devices, Rig States,
 * Lights, Pages and Pre-flight - the OPBridge pill, the Inspector's links, the
 * page pills, first run. Those switches stay the source of truth: App maps
 * whichever is on to Navigate's section (`paneFromStores`) and opens Navigate
 * there, and closing Navigate turns them all off (`closePaneStores`). So every
 * way in lands in Navigate without rewiring a single caller.
 */

import {
  settingsOpen, settingsPane, openSettingsPane, closeSettings,
  connectOpen, openConnect, closeConnect,
  connectedDevicesOpen, openConnectedDevices, closeConnectedDevices,
  closeTrafficMonitor,
} from '../settings/store.ts';
import { rigStateManagerOpen, openRigStateManager, closeRigStateManager } from '../rigState/store.ts';
import { lightsManagerOpen, openLightsManager, closeLightsManager } from '../components/LightsManager.tsx';
import { pageManagerOpen, openPageManager, closePageManager } from '../layout/pageManagerStore.ts';
import { preflightOpen, openPreflight, closePreflight } from '../performance/preflightStore.ts';
import { helpOpen, openHelp, closeHelp } from '../help/store.ts';
import { setNavigatePane, type NavPane } from './store.ts';

/** The section an open switch asks for, or null when none is on. */
export function paneFromStores(): NavPane | null {
  // Help first: it opens OVER whatever section asked for it (Settings' Help
  // rows, an article's "Still stuck?"), and closing it lands back there.
  if (helpOpen()) return 'help';
  if (connectOpen()) return 'connect';
  if (connectedDevicesOpen()) return 'devices';
  if (rigStateManagerOpen()) return 'rigstates';
  if (lightsManagerOpen()) return 'lights';
  if (pageManagerOpen()) return 'pages';
  if (preflightOpen()) return 'preflight';
  if (settingsOpen()) return settingsPane();
  return null;
}

/** Turn every section switch off (Navigate closed, or another section chosen). */
export function closePaneStores(): void {
  closeConnect();
  closeConnectedDevices();
  closeRigStateManager();
  closeLightsManager();
  closePageManager();
  closePreflight();
  closeSettings();
  closeTrafficMonitor();
  closeHelp();
}

/** Show a section in Navigate: its switch on (if it has one), the others off. */
export function showPane(p: NavPane): void {
  closePaneStores();
  switch (p) {
    case 'connect': openConnect(); break;
    case 'devices': openConnectedDevices(); break;
    case 'rigstates': openRigStateManager(); break;
    case 'lights': openLightsManager(); break;
    case 'pages': openPageManager(); break;
    case 'preflight': openPreflight(); break;
    case 'settings': case 'look': case 'backup': openSettingsPane(p); break;
    case 'help': openHelp(); break;
    default: break;
  }
  setNavigatePane(p);
}
